import * as dayjs from 'dayjs';
import { TimeRecordStatus } from './TimeRecordStatus';
import { TimeRecordActivity, TimeRecordConstants, TimeRecordStatusNumber } from '../constant/time-record-constants';
import { DateTimeHelper, DateTimeType } from '../utils/date-time-helper';
import { TimeRecordTypeId } from '../constant/time-record-constants/time-record-type.constants';

export class TimeRecord {
  RecordId?: number;

  Id?: number;
  CreatedBy?: number;
  CreatedDate?: string;
  ModifiedBy?: number;
  ModifiedDate?: string;
  ActivityLogId?: number;

  // Base
  EmployeeId: number;
  EmployeeNumber: string;
  Activity: TimeRecordActivity;
  ProjectId: number;
  TaskId: number;
  LaborCategoryId: number;
  FIPSStateId: number;
  ActivityLogRecordId: number;
  OrganizationId: number;
  ClockIn: string;
  ClockInOffset: number;
  ClockInTimeZoneId: string;
  CapturedClockIn: string;
  CapturedClockInOffset: number;
  CapturedClockInTimeZoneId: string;
  InputType: string;
  StatusId: number;
  IsDeleted: boolean;
  IsArchived: boolean;

  // Open
  ValidationMessage?: string;

  // Closed
  ClockOut?: string;
  ClockOutOffset?: number;
  ClockOutTimeZoneId?: string;
  CapturedClockOut?: string;
  CapturedClockOutOffset?: number;
  CapturedClockOutTimeZoneId?: string;

  // Submitted
  SubmittedBy?: number;
  SubmittedDate?: string;
  SubmittedDateOffset?: number;
  SubmittedTimeZoneId?: string;

  // Verified
  VerifiedBy?: number;
  VerifiedDate?: string;
  VerifiedDateOffset?: number;
  VerifiedTimeZoneId?: string;

  // Approved
  ApprovedBy?: number;
  ApprovedDate?: string;
  ApprovedDateOffset?: number;
  ApprovedTimeZoneId?: string;

  // Locked
  LockedBy?: number;
  LockedDate?: string;
  LockedDateOffset?: number;
  LockedTimeZoneId?: string;

  // Returned
  ReturnedBy?: number;
  ReturnedDate?: string;
  ReturnedDateOffset?: number;
  ReturnedTimeZoneId?: string;

  TimecardId?: number;
  TypeId?: TimeRecordTypeId;

  constructor(obj?: Partial<TimeRecord>) {
    if (obj) {
      Object.assign(this, obj);
    }
    else {
      throw Error('Object cannot be null');
    }
  }

  clone(): TimeRecord {
    return new TimeRecord(this);
  }

  setAssignmentFields(projectId: number, taskId: number, laborCategoryId: number, FIPSStateId: number, activityLogRecordId: number, activityLogId: number | null): void {
    this.ProjectId = projectId;
    this.TaskId = taskId;
    this.LaborCategoryId = laborCategoryId;
    this.FIPSStateId = FIPSStateId;
    this.ActivityLogRecordId = activityLogRecordId;
    this.ActivityLogId = activityLogId;
  }

  setClockInFields(clockIn: DateTimeType, clockInTimeZoneId: string, capturedClockIn: DateTimeType, capturedClockInTimeZoneId: string): void {
    this.ClockIn = DateTimeHelper.formatDateTime(clockIn);
    this.ClockInOffset = DateTimeHelper.getUtcOffsetInMinutes(clockIn, clockInTimeZoneId);
    this.ClockInTimeZoneId = clockInTimeZoneId;
    this.CapturedClockIn = DateTimeHelper.formatDateTime(capturedClockIn);
    this.CapturedClockInOffset = DateTimeHelper.getUtcOffsetInMinutes(capturedClockIn, capturedClockInTimeZoneId);
    this.CapturedClockInTimeZoneId = capturedClockInTimeZoneId;
  }

  setClockOutFields(clockOut: DateTimeType, clockOutTimeZoneId: string, capturedClockOut: DateTimeType, capturedClockOutTimeZoneId: string): void {
    this.ClockOut = DateTimeHelper.formatDateTime(clockOut);
    this.ClockOutOffset = DateTimeHelper.getUtcOffsetInMinutes(clockOut, clockOutTimeZoneId);
    this.ClockOutTimeZoneId = clockOutTimeZoneId;
    this.CapturedClockOut = DateTimeHelper.formatDateTime(capturedClockOut);
    this.CapturedClockOutOffset = DateTimeHelper.getUtcOffsetInMinutes(capturedClockOut, capturedClockOutTimeZoneId);
    this.CapturedClockOutTimeZoneId = capturedClockOutTimeZoneId;
  }

  setToOpen(timeRecordStatus: TimeRecordStatus): void {
    if (timeRecordStatus.Number !== TimeRecordStatusNumber.Open) {
      throw new Error("Status must be Open");
    }

    this.StatusId = timeRecordStatus.Id;

    this.clearClosedFields();
    this.clearReturnedFields();
    this.clearSubmittedFields();
    this.clearVerifiedFields();
    this.clearApprovedFields();
    this.clearLockedFields();
  }

  setToClosed(
    pendingOverrideStatus: TimeRecordStatus,
    pendingVerificationStatus: TimeRecordStatus,
    clockOut: DateTimeType,
    clockOutTimeZoneId: string,
    capturedClockOut: DateTimeType,
    capturedClockOutTimeZoneId: string
  ): void {
    this.setClockOutFields(clockOut, clockOutTimeZoneId, capturedClockOut, capturedClockOutTimeZoneId);

    if (this.hasOverrideCondition()) {
      if (pendingOverrideStatus.Number !== TimeRecordStatusNumber.PendingOverride) {
        throw new Error("Time record has override condition. Status must be PendingOverride");
      }
      this.StatusId = pendingOverrideStatus.Id;
    } else {
      if (pendingVerificationStatus.Number !== TimeRecordStatusNumber.PendingVerification) {
        throw new Error("Time record has no override condition. Status must be PendingVerification");
      }
      this.StatusId = pendingVerificationStatus.Id;
    }

    this.clearReturnedFields();
    this.clearSubmittedFields();
    this.clearVerifiedFields();
    this.clearApprovedFields();
    this.clearLockedFields();
  }

  private clearClosedFields(): void {
    this.ClockOut = undefined;
    this.ClockOutOffset = undefined;
    this.CapturedClockOut = undefined;
    this.CapturedClockOutOffset = undefined;
    this.ClockOutTimeZoneId = undefined;
  }

  setToSubmitted(
    pendingOverrideStatus: TimeRecordStatus,
    pendingVerificationStatus: TimeRecordStatus,
    submittedBy: number,
    submittedDate: DateTimeType,
    submittedTimeZoneId: string
  ): void {
    this.SubmittedBy = submittedBy;
    this.SubmittedDate = DateTimeHelper.formatDateTime(submittedDate);
    this.SubmittedDateOffset = DateTimeHelper.getUtcOffsetInMinutes(submittedDate, submittedTimeZoneId);
    this.SubmittedTimeZoneId = submittedTimeZoneId;

    if (this.hasOverrideCondition()) {
      if (pendingOverrideStatus.Number !== TimeRecordStatusNumber.PendingOverride) {
        throw new Error("Time record has override condition. Status must be PendingOverride");
      }
      this.StatusId = pendingOverrideStatus.Id;
    } else {
      if (pendingVerificationStatus.Number !== TimeRecordStatusNumber.PendingVerification) {
        throw new Error("Time record has no override condition. Status must be PendingVerification");
      }
      this.StatusId = pendingVerificationStatus.Id;
    }

    this.clearReturnedFields();
    this.clearVerifiedFields();
    this.clearApprovedFields();
    this.clearLockedFields();
  }

  private clearSubmittedFields(): void {
    this.SubmittedBy = undefined;
    this.SubmittedDate = undefined;
    this.SubmittedDateOffset = undefined;
    this.SubmittedTimeZoneId = undefined;
  }

  setToVerified(
    timeRecordStatus: TimeRecordStatus,
    verifiedBy: number,
    verifiedDate: DateTimeType,
    verifiedTimeZoneId: string
  ): void {
    this.VerifiedBy = verifiedBy;
    this.VerifiedDate = DateTimeHelper.formatDateTime(verifiedDate);
    this.VerifiedDateOffset = DateTimeHelper.getUtcOffsetInMinutes(verifiedDate, verifiedTimeZoneId);
    this.VerifiedTimeZoneId = verifiedTimeZoneId;

    if (timeRecordStatus.Number !== TimeRecordStatusNumber.Verified) {
      throw new Error("Status must be Verified");
    }

    this.StatusId = timeRecordStatus.Id;

    this.clearReturnedFields();
    this.clearApprovedFields();
    this.clearLockedFields();
  }

  private clearVerifiedFields(): void {
    this.VerifiedBy = undefined;
    this.VerifiedDate = undefined;
    this.VerifiedDateOffset = undefined;
    this.VerifiedTimeZoneId = undefined;
  }

  setToApproved(
    timeRecordStatus: TimeRecordStatus,
    approvedBy: number,
    approvedDate: DateTimeType,
    approvedTimeZoneId: string
  ): void {
    this.ApprovedBy = approvedBy;
    this.ApprovedDate = DateTimeHelper.formatDateTime(approvedDate);
    this.ApprovedDateOffset = DateTimeHelper.getUtcOffsetInMinutes(approvedDate, approvedTimeZoneId);
    this.ApprovedTimeZoneId = approvedTimeZoneId;

    if (timeRecordStatus.Number !== TimeRecordStatusNumber.Approved) {
      throw new Error("Status must be Approved");
    }

    this.StatusId = timeRecordStatus.Id;

    this.clearReturnedFields();
    this.clearLockedFields();
  }

  private clearApprovedFields(): void {
    this.ApprovedBy = undefined;
    this.ApprovedDate = undefined;
    this.ApprovedDateOffset = undefined;
    this.ApprovedTimeZoneId = undefined;
  }

  setToLocked(
    timeRecordStatus: TimeRecordStatus,
    lockedBy: number,
    lockedDate: DateTimeType,
    lockedTimeZoneId: string
  ): void {
    this.LockedBy = lockedBy;
    this.LockedDate = DateTimeHelper.formatDateTime(lockedDate);
    this.LockedDateOffset = DateTimeHelper.getUtcOffsetInMinutes(lockedDate, lockedTimeZoneId);
    this.LockedTimeZoneId = lockedTimeZoneId;

    if (timeRecordStatus.Number !== TimeRecordStatusNumber.Locked) {
      throw new Error("Status must be Locked");
    }

    this.StatusId = timeRecordStatus.Id;

    this.clearReturnedFields();
  }

  private clearLockedFields(): void {
    this.LockedBy = undefined;
    this.LockedDate = undefined;
    this.LockedDateOffset = undefined;
    this.LockedTimeZoneId = undefined;
  }

  setToReturned(
    timeRecordStatus: TimeRecordStatus,
    returnedBy: number,
    returnedDate: DateTimeType,
    returnedTimeZoneId: string
  ): void {
    this.ReturnedBy = returnedBy;
    this.ReturnedDate = DateTimeHelper.formatDateTime(returnedDate);
    this.ReturnedDateOffset = DateTimeHelper.getUtcOffsetInMinutes(returnedDate, returnedTimeZoneId);
    this.ReturnedTimeZoneId = returnedTimeZoneId;

    if (timeRecordStatus.Number !== TimeRecordStatusNumber.Returned) {
      throw new Error("Status must be Returned");
    }

    this.StatusId = timeRecordStatus.Id;

    this.clearVerifiedFields();
    this.clearApprovedFields();
    this.clearLockedFields();
  }

  private clearReturnedFields(): void {
    this.ReturnedBy = undefined;
    this.ReturnedDate = undefined;
    this.ReturnedDateOffset = undefined;
    this.ReturnedTimeZoneId = undefined;
  }

  setSoftDeleted(): void {
    this.IsDeleted = true;
  }

  isSoftDeleted(): boolean {
    return this.IsDeleted;
  }

  hasClockInOverride(): boolean {
    return !DateTimeHelper.checkTimeDifferenceLessThan("minute", TimeRecordConstants.PENDING_OVERRIDE_THRESHOLD_MINUTES, this.ClockIn, this.ClockInTimeZoneId, this.CapturedClockIn, this.CapturedClockInTimeZoneId);
  }

  hasClockOutOverride(): boolean {
    return this.ClockOut != null
      && this.ClockOutTimeZoneId != null
      && this.CapturedClockOut != null
      && this.CapturedClockOutTimeZoneId != null
      && !DateTimeHelper.checkTimeDifferenceLessThan("minute", TimeRecordConstants.PENDING_OVERRIDE_THRESHOLD_MINUTES, this.ClockOut, this.ClockOutTimeZoneId, this.CapturedClockOut, this.CapturedClockOutTimeZoneId);
  }

  hasOverrideCondition(): boolean {
    return this.hasClockInOverride() || this.hasClockOutOverride();
  }

  get Date(): string {
      return DateTimeHelper.formatDateTimeAsDate(this.ClockIn);
  }

  get VerifiedDateUtc(): dayjs.Dayjs {
    return this.VerifiedDate ? dayjs(this.VerifiedDate).add(this.VerifiedDateOffset, 'm') : null;
  }

  get ApprovedDateUtc(): dayjs.Dayjs {
    return this.ApprovedDate ? dayjs(this.ApprovedDate).add(this.ApprovedDateOffset, 'm') : null;
  }
}

